import axiosInstance from "@/api/axiosInstance";
import {
  Campaign,
  CampaignReason,
  CampaignRecipient,
  CampaignResults,
  MoosendSyncResult,
  RecipientInput,
  RecipientUpsertResult,
} from "@/common/model/Campaign";

const BASE = "/api/admin/winback";

export const CampaignService = {
  // Campaigns
  async list(): Promise<Campaign[]> {
    const r = await axiosInstance.get(`${BASE}/campaigns`);
    return r.data;
  },
  async get(id: number): Promise<Campaign> {
    const r = await axiosInstance.get(`${BASE}/campaigns/${id}`);
    return r.data;
  },
  async create(data: Partial<Campaign>): Promise<{ id: number }> {
    const r = await axiosInstance.post(`${BASE}/campaigns`, data);
    return r.data;
  },
  async update(id: number, data: Partial<Campaign>): Promise<{ id: number }> {
    const r = await axiosInstance.put(`${BASE}/campaigns/${id}`, data);
    return r.data;
  },
  async remove(id: number): Promise<void> {
    await axiosInstance.delete(`${BASE}/campaigns/${id}`);
  },

  // Reasons
  async listReasons(campaignId: number): Promise<CampaignReason[]> {
    const r = await axiosInstance.get(`${BASE}/campaigns/${campaignId}/reasons`);
    return r.data;
  },
  async createReason(campaignId: number, data: Partial<CampaignReason>): Promise<{ id: number }> {
    const r = await axiosInstance.post(`${BASE}/campaigns/${campaignId}/reasons`, data);
    return r.data;
  },
  async updateReason(reasonId: number, data: Partial<CampaignReason>): Promise<{ id: number }> {
    const r = await axiosInstance.put(`${BASE}/reasons/${reasonId}`, data);
    return r.data;
  },
  async deleteReason(reasonId: number): Promise<void> {
    await axiosInstance.delete(`${BASE}/reasons/${reasonId}`);
  },

  // Recipients
  async upsertRecipients(campaignId: number, rows: RecipientInput[]): Promise<RecipientUpsertResult> {
    const r = await axiosInstance.post(`${BASE}/campaigns/${campaignId}/recipients`, rows);
    return r.data;
  },
  async listRecipients(campaignId: number): Promise<CampaignRecipient[]> {
    const r = await axiosInstance.get(`${BASE}/campaigns/${campaignId}/recipients`);
    return r.data;
  },

  // Results
  async results(campaignId: number): Promise<CampaignResults> {
    const r = await axiosInstance.get(`${BASE}/campaigns/${campaignId}/results`);
    return r.data;
  },

  // Moosend
  async syncMoosend(campaignId: number): Promise<MoosendSyncResult> {
    // Send an explicit empty body so a Content-Length is always set (the App Engine
    // dev server returns 411 for a POST with no body).
    const r = await axiosInstance.post(`${BASE}/campaigns/${campaignId}/sync-moosend`, {});
    return r.data;
  },
};

/**
 * Parse a pasted/uploaded CSV (one "userid,monthlyUrl,annualUrl" per line) into recipient rows.
 * Tolerates a header line, blank lines, and surrounding whitespace.
 */
export function parseRecipientCsv(text: string): { rows: RecipientInput[]; errors: string[] } {
  const rows: RecipientInput[] = [];
  const errors: string[] = [];
  text
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter((l) => l.length > 0)
    .forEach((line, i) => {
      const parts = line.split(",").map((p) => p.trim());
      const userId = Number(parts[0]);
      if (!Number.isInteger(userId)) {
        // Skip a header row silently; report other bad lines.
        if (!/^userid$/i.test(parts[0])) {
          errors.push(`Line ${i + 1}: "${line}" — first column is not a numeric userid`);
        }
        return;
      }
      rows.push({
        userId,
        offerUrlMonthly: parts[1] || undefined,
        offerUrlAnnual: parts[2] || undefined,
      });
    });
  return { rows, errors };
}
